import AlertSnackbar from "@/components/AlertSnackbar";
import { useCurrentUser } from "@/services/hooks";
import { uploadUserFile } from "@/services/upload";
import { Edit } from "@mui/icons-material";
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import ReactPlayer from "react-player";

export default function EditPreviewVideo() {
  const { data: currentUser, isLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const metaKey = "preview";

  const [file, setFile] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const url = file ? URL.createObjectURL(file) : currentUser?.meta?.[metaKey];

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    mutation.mutate(selectedFile);
  };

  const mutation = useMutation({
    mutationFn: (blob) => uploadUserFile(blob, metaKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["current_user"] });
      setShowSnackbar(true);
    },
  });

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        height="500px"
        width="100%"
        sx={{ borderRadius: "20px", my: 2 }}
      />
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <AlertSnackbar
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
      />
      <Box sx={{ position: "relative" }}>
        <Card
          sx={{
            borderRadius: "20px",
            opacity: mutation.isPending ? 0.5 : 1,
            transition: "all 0.2s ease-in-out",
            width: "100%",
            aspectRatio: "1/1",
            overflow: "hidden",
          }}
        >
          {url ? (
            <Box sx={{ position: "relative", height: "100%" }}>
              <ReactPlayer
                url={url}
                controls
                height="100%"
                width="100%"
                style={{ borderRadius: "20px" }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No preview video uploaded yet
              </Typography>
            </Box>
          )}
        </Card>

        <label htmlFor={metaKey}>
          <IconButton
            component="span"
            disabled={mutation.isPending}
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
              },
            }}
          >
            {mutation.isPending ? (
              <CircularProgress size={20} color="secondary" />
            ) : (
              <Edit />
            )}
          </IconButton>
        </label>
      </Box>
      <input
        accept="video/*"
        style={{ display: "none" }}
        id={metaKey}
        type="file"
        onChange={handleFileChange}
      />
    </Box>
  );
}
